import { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/UI/Layout';
import Navbar from './components/UI/Navbar';
import Footer from './components/UI/Footer';
import PrivacyModal from './components/UI/PrivacyModal';
import WhatsAppFloat from './components/UI/WhatsAppFloat';
import CookieConsent from './components/UI/CookieConsent';
import { useTranslation } from 'react-i18next';
import { useDeviceTier } from './hooks/useDeviceTier';

// Páginas importadas de forma estática — igual que en apple.com, sin pantalla de
// carga entre secciones. Van todas en el bundle inicial en vez de un chunk por
// ruta; para el tamaño de este sitio es la mejor opción: la navegación interna
// nunca muestra un estado de carga.
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import UseCasesPage from './pages/UseCasesPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import PilotProgramPage from './pages/PilotProgramPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';

// La escena 3D (galactic) sí se queda lazy: es pesada (three.js + post-procesado)
// y solo se monta si el usuario activa ese tema — no forma parte de la navegación
// normal, así que no reintroduce el problema de la pantalla de carga entre páginas.
const Scene3D = lazy(() => import('./components/Three/Scene3D'));

function App() {
  const deviceTier = useDeviceTier();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light' | 'galactic'>('dark');
  const [activeTab, setActiveTab] = useState<'crm' | 'ia'>('crm');
  const [isNavbarHidden] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Both 'dark' and 'galactic' use dark mode CSS variables
    document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : 'dark');
  }, [theme]);

  // Pequeño respiro tras el primer paint antes de montar el fondo 3D (galactic),
  // para no competir con el render inicial de la página.
  useEffect(() => {
    const timer = setTimeout(() => setIsAppReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'dark') return 'light';
      if (prev === 'light') return 'galactic';
      return 'dark';
    });
  };

  const handlePageChange = (page: string, section?: string) => {
    // If not on the requested page, navigate there
    if (location.pathname !== `/${page}` && !(page === 'home' && location.pathname === '/')) {
       navigate(page === 'home' ? '/' : `/${page}`);
    }

    if (section) {
      // Un solo setTimeout(300) fijo no alcanzaba: si el home (u otra página
      // con secciones ancla) todavía está montando/acomodando contenido
      // pesado (videos, imágenes) el elemento puede no existir todavía, o la
      // posición calculada queda vieja apenas un instante después por un
      // salto de layout — terminaba scrolleando a cualquier lado, incluido
      // el final de la página. Se reintenta hasta encontrar el elemento
      // (hasta 2s) y se repite el scroll una vez más pasado un momento, para
      // corregir cualquier corrimiento de layout que haya ocurrido después.
      let attempts = 0;
      const tryScroll = () => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 500);
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(tryScroll, 100);
        }
      };
      setTimeout(tryScroll, 200);
    } else {
      // Se llama dos veces a propósito: la primera corre ya mismo (cubre el
      // caso de clickear un link estando ya en esa ruta, donde el useEffect
      // de location.pathname no se vuelve a disparar porque el pathname no
      // cambió), y la segunda después de que la página nueva termine de
      // montarse/animarse (cubre la carrera entre navigate() y el render,
      // que a veces dejaba el scroll a mitad de camino en vez de en 0).
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 350);
    }
  };

  // Determinar currentPage para el Navbar basado en la URL
  const currentPage = location.pathname.includes('nosotros') ? 'about' : 
                      location.pathname.includes('servicios') ? 'services' :
                      location.pathname.includes('casos-uso') ? 'usecases' :
                      location.pathname.includes('blog') ? 'blog' :
                      location.pathname.includes('contacto') ? 'contact' :
                      location.pathname.includes('login') ? 'login' : 'home';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
      }}
      style={{ position: 'relative', willChange: 'opacity' }}
    >
      <Layout>
        <a href="#main-content" className="skip-link">{t('a11y.skip_link', 'Saltar al contenido')}</a>
        <Navbar
          theme={theme}
          onToggleTheme={toggleTheme}
          isHidden={isNavbarHidden}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />

        {isAppReady && theme === 'galactic' && deviceTier !== 'off' && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: 'var(--bg-dark)'
          }}>
            <Suspense fallback={null}>
              {/* Scene3D expects 'dark' or 'light', so we pass 'dark' for galactic mode */}
              <Scene3D theme="dark" lowPerf={deviceTier === 'low'} />
            </Suspense>
          </div>
        )}
        {isAppReady && (theme !== 'galactic' || deviceTier === 'off') && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: 'var(--bg-dark)'
          }} />
        )}

        <div id="main-content" style={{ position: 'relative', zIndex: 1 }}>
          {/* Sin mode="wait": con "wait" el router ya actualizó la URL/useParams
              pero el DOM se queda pegado en la ruta vieja hasta que termine la
              animación de salida — si esa animación no llega a resolver (pestaña
              sin foco, prefers-reduced-motion, navegación rápida) el usuario ve
              contenido viejo con una URL nueva. Mismo bug ya visto y corregido en
              el filtro de categorías del blog (BlogPage.tsx). */}
          <AnimatePresence>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<HomePage theme={theme === 'light' ? 'light' : 'dark'} activeTab={activeTab} setActiveTab={setActiveTab} />} />
              <Route path="/nosotros" element={<AboutPage />} />
              <Route path="/servicios" element={<ServicesPage />} />
              <Route path="/casos-uso" element={<UseCasesPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/contacto" element={<ContactPage />} />
              <Route path="/piloto" element={<PilotProgramPage />} />
              <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
              <Route path="/terminos-condiciones" element={<TermsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Footer onOpenPrivacy={() => setShowPrivacy(true)} />
          </motion.div>
        </div>

        <PrivacyModal
          isOpen={showPrivacy}
          onClose={() => setShowPrivacy(false)}
        />
        <WhatsAppFloat />
        <CookieConsent />
      </Layout>
    </motion.div>
  );
}

export default App;
